import React, { useState, useEffect, useCallback } from "react";
import { processService } from "../../services/process.service";
import { userService } from "../../services/user.service";
import { Card } from "../ui/card";
import { toast } from "sonner";
import { CedentesList } from "./cedentes/CedentesList";
import { CedenteActions } from "./cedentes/CedenteActions";
import { CedenteDialog } from "./cedentes/CedenteDialog";
import useDebounce from "../../hooks/useDebounce";

const { importCedentesCSV } = userService;
const { unlinkCedente, getProcessoCedentes } = processService;

export function CedentesTab({ processoId }) {
    const [processoCedentes, setProcessoCedentes] = useState([]);
    const [loadingCedentes, setLoadingCedentes] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [linkedSearch, setLinkedSearch] = useState("");
    const debouncedLinkedSearch = useDebounce(linkedSearch, 1000);

    const [isCedenteDialogOpen, setIsCedenteDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const loadProcessoCedentes = useCallback(async () => {
        if (!processoId) return;
        try {
            setLoadingCedentes(true);
            const offset = (page - 1) * pageSize;
            const data = await getProcessoCedentes(processoId, debouncedLinkedSearch, pageSize, offset);
            setProcessoCedentes(data.items);
            setTotalItems(data.total);
        } catch (e) {
            console.error("Erro ao carregar cedentes vinculados", e);
            toast.error("Erro ao carregar substituídos vinculados.");
        } finally {
            setLoadingCedentes(false);
        }
    }, [processoId, page, pageSize, debouncedLinkedSearch]);

    useEffect(() => {
        loadProcessoCedentes();
    }, [loadProcessoCedentes, page, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [debouncedLinkedSearch]);

    const handleUnlinkCedente = useCallback(async (cedenteId) => {
        if (!window.confirm("Tem certeza que deseja desvincular este substituído?")) return;
        try {
            await unlinkCedente(processoId, cedenteId);
            toast.success("Substituído desvinculado");
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao desvincular substituído");
        }
    }, [processoId, loadProcessoCedentes]);

    const handleBulkUnlink = useCallback(async (cedenteIds) => {
        if (!window.confirm(`Tem certeza que deseja desvincular ${cedenteIds.length} substituídos selecionados?`)) return;
        try {
            await processService.unlinkCedentesBulk(processoId, cedenteIds);
            toast.success(`${cedenteIds.length} substituídos desvinculados com sucesso!`);
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao desvincular substituídos em lote: " + e.message);
        }
    }, [processoId, loadProcessoCedentes]);

    const handleImportCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsSaving(true);
        try {
            await importCedentesCSV(processoId, file);
            toast.success("CSV importado com sucesso!");
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao importar CSV: " + e.message);
        } finally {
            setIsSaving(false);
            e.target.value = null;
        }
    };

    const handleEdit = useCallback((processoCedente) => {
        setEditingItem(processoCedente);
        setIsCedenteDialogOpen(true);
    }, []);

    const handleDialogOpenChange = useCallback((open) => {
        setIsCedenteDialogOpen(open);
        if (!open) setEditingItem(null);
    }, []);

    const handleExportCSV = useCallback(async () => {
        if (!processoId) return;
        setIsExporting(true);
        try {
            const blob = await processService.exportProcessoCedentesCSV(processoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `cedentes_processo_${processoId}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao exportar: " + e.message);
        } finally {
            setIsExporting(false);
        }
    }, [processoId]);

    const handleDownloadTemplate = useCallback(() => {
        const headers = "cpf,nome,rg,email,telefone,banco,agencia,conta_corrente,digito_conta,pix,tipo_pix,endereco_logradouro,endereco_numero,endereco_complemento,endereco_bairro,endereco_cidade,endereco_estado,endereco_cep,tipo_conta,estado_civil,nome_conjuge,cpf_conjuge,valor_avista,valor_parcelado,parcelas,valor_parcela,valor_sindicato,valor_contador,valor_proposta_final,valor_parcela_primeiro_periodo,valor_parcela_segundo_periodo,valor_parcela_terceiro_periodo,valor_isonomia,valor_pdv,valor_fgts,tipo_aceite";
        const example = "12345678900,João Silva,1234567,joao@example.com,11999999999,001,1234,12345,6,12345678900,cpf,Rua A,123,Apto 1,Centro,São Paulo,SP,01000000,corrente,solteiro,,,10000,12000,12,1000,500,200,9000,,,,,";
        const content = `${headers}\n${example}`;

        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "modelo_importacao_cedentes.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    return (
        <Card className="p-6">
            <CedenteActions
                onDownloadTemplate={handleDownloadTemplate}
                onImportCSV={handleImportCSV}
                onOpenDialog={setIsCedenteDialogOpen}
                isSaving={isSaving}
                onExportCSV={handleExportCSV}
                isExporting={isExporting}
            />

            <CedentesList
                cedentes={processoCedentes}
                loading={loadingCedentes}
                totalItems={totalItems}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onUnlink={handleUnlinkCedente}
                onBulkUnlink={handleBulkUnlink}
                onEdit={handleEdit}
                searchQuery={linkedSearch}
                onSearch={setLinkedSearch}
                isDebouncing={linkedSearch !== debouncedLinkedSearch}
            />

            <CedenteDialog
                isOpen={isCedenteDialogOpen}
                onOpenChange={handleDialogOpenChange}
                processoId={processoId}
                onSuccess={loadProcessoCedentes}
                editingItem={editingItem}
            />
        </Card>
    );
}
